/**
 * Mobile Document Capture Component
 * Supports camera capture and file selection for document upload
 */

import React, { useState, useRef } from 'react';
import { Camera, Image, FileText, X, Upload, Check, Loader2 } from 'lucide-react';
import { mobileAPI } from '../../api';

const DOCUMENT_TYPES = [
  { id: 'photo', label: 'Photo', icon: Image },
  { id: 'carte_grise', label: 'Carte grise', icon: FileText },
  { id: 'assurance', label: 'Assurance', icon: FileText },
  { id: 'controle_technique', label: 'Controle technique', icon: FileText },
  { id: 'facture', label: 'Facture', icon: FileText },
  { id: 'autre', label: 'Autre', icon: FileText },
];

export default function DocumentCapture({
  vehicleId,
  referenceId,
  scope = 'VEHICLE',
  todoId = null,
  onSuccess,
  onCancel
}) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [documentType, setDocumentType] = useState('photo');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError('');

      // Create preview for images
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setPreview(reader.result);
        reader.readAsDataURL(selectedFile);
      } else {
        setPreview(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Veuillez selectionner un fichier');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const result = await mobileAPI.uploadDocument(file, {
        scope,
        reference_id: referenceId,
        document_type: documentType,
        vehicle_id: vehicleId,
        todo_id: todoId,
        notes: notes || undefined,
      });

      if (result.ok || result.document) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess?.(result.document);
        }, 1000);
      } else {
        setError(result.error || 'Erreur lors du telechargement');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Erreur de connexion');
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-slate-900/95 z-50 flex items-center justify-center p-4">
        <div className="bg-green-500/20 border border-green-500/50 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-white" />
          </div>
          <p className="text-green-400 font-medium text-lg">Document envoye!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-900/95 z-50 flex flex-col">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-3 flex items-center justify-between safe-area-top">
        <button onClick={onCancel} className="p-2 -ml-2 text-slate-400">
          <X className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold text-white">Ajouter un document</h1>
        <div className="w-10" />
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Capture buttons */}
        {!file && (
          <div className="grid grid-cols-2 gap-3">
            {/* Camera button */}
            <button
              onClick={() => cameraInputRef.current?.click()}
              className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl p-6 flex flex-col items-center gap-2 transition-colors"
            >
              <Camera className="w-10 h-10" />
              <span className="font-medium">Prendre photo</span>
            </button>

            {/* Gallery button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-slate-700 hover:bg-slate-600 text-white rounded-xl p-6 flex flex-col items-center gap-2 transition-colors"
            >
              <Image className="w-10 h-10" />
              <span className="font-medium">Galerie / Fichier</span>
            </button>

            {/* Hidden file inputs */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        )}

        {/* Preview */}
        {file && (
          <div className="relative">
            {preview ? (
              <div className="relative rounded-xl overflow-hidden bg-slate-800">
                <img src={preview} alt="Preview" className="w-full h-64 object-contain" />
                <button
                  onClick={clearFile}
                  className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="bg-slate-800 rounded-xl p-6 flex items-center gap-4">
                <FileText className="w-12 h-12 text-slate-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{file.name}</p>
                  <p className="text-slate-400 text-sm">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <button onClick={clearFile} className="text-red-400 p-2">
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Document type selector */}
        {file && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Type de document</label>
            <div className="grid grid-cols-2 gap-2">
              {DOCUMENT_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = documentType === type.id;
                return (
                  <button
                    key={type.id}
                    onClick={() => setDocumentType(type.id)}
                    className={`p-3 rounded-lg flex items-center gap-2 transition-colors ${
                      isSelected
                        ? 'bg-blue-500 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Notes field */}
        {file && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Notes (optionnel)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ajouter une description..."
              rows={3}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Footer */}
      {file && (
        <div className="bg-slate-800 border-t border-slate-700 p-4 safe-area-bottom">
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Envoyer le document
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
